import { createContext, useContext, useEffect } from 'react'

/**
 * Whether the document route is showing the full-height editor workspace (UX-26) or a normal page
 * such as the Done view. The shell hides its frame (phone top bar and bottom nav) only for the
 * workspace, so a completed document is never a dead end on a phone.
 */
export const WorkspaceFrameContext = createContext<(page: boolean) => void>(() => undefined)

/** Called by the document route: `page` true means show the normal app frame. */
export function useDocumentPageFrame(page: boolean): void {
  const setPage = useContext(WorkspaceFrameContext)
  useEffect(() => {
    setPage(page)
    return () => setPage(false)
  }, [page, setPage])
}

/**
 * A step of a flow whose earlier steps ran in the full-screen workspace (Review and send, step 3 of
 * 3): the phone bottom nav stays hidden there too, so it doesn't come back for the last step only.
 * The top bar's back control remains the way out. Marked on the root, like the keyboard's state.
 */
export function FlowStepFrame(): null {
  useEffect(() => {
    const root = document.documentElement
    root.dataset['flowStep'] = 'on'
    return () => {
      delete root.dataset['flowStep']
    }
  }, [])
  return null
}
